import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { MapPin, Clock, Filter, ChevronDown, ChevronUp } from "lucide-react";
import axios from "axios";
import Navbar from "@/components/Navbar";
import LeadModal from "@/components/LeadModal";
import Footer from "@/components/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DEFAULT_IMG = "https://images.unsplash.com/photo-1469474968028-56623f02e42e?w=800&q=80";

const CATEGORIES = ["All", "Adrenaline & Exploration", "Intimate Escapes", "Elevated Indulgence", "Cultural Immersions", "Nature & Solitude", "Rhythm & Celebration"];
const ARCHETYPES = ["All", "Explorer", "Romantic", "Celebrator", "Seeker", "Sensorialist", "Soul Traveler"];

const CATEGORY_COLORS = {
  "Adrenaline & Exploration": "#E8A838",
  "Intimate Escapes": "#E8738A",
  "Elevated Indulgence": "#F97316",
  "Cultural Immersions": "#06B6D4",
  "Nature & Solitude": "#22C55E",
  "Rhythm & Celebration": "#A855F7"
};

// Get first complete sentence for preview
function getPreviewText(desc) {
  const match = desc.match(/^[^.!?]+[.!?]/);
  return match ? match[0] : desc.substring(0, 120) + (desc.length > 120 ? "." : "");
}

export default function Experiences() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [experiences, setExperiences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [prefillExp, setPrefillExp] = useState("");
  const [prefillArch, setPrefillArch] = useState("");
  const [filterOpen, setFilterOpen] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  const selectedCategory = searchParams.get("category") || "All";
  const selectedArchetype = searchParams.get("archetype") || "All";

  useEffect(() => {
    const params = {};
    if (selectedCategory !== "All") params.category = selectedCategory;
    if (selectedArchetype !== "All") params.archetype = selectedArchetype;
    setLoading(true);
    setExpandedId(null);
    axios.get(`${API}/experiences`, { params })
      .then(res => setExperiences(res.data))
      .catch(() => setExperiences([]))
      .finally(() => setLoading(false));
  }, [selectedCategory, selectedArchetype]);

  const setFilter = (key, value) => {
    const newParams = new URLSearchParams(searchParams);
    if (value === "All") newParams.delete(key);
    else newParams.set(key, value);
    setSearchParams(newParams);
  };

  const openLead = (exp = null) => {
    setPrefillExp(exp?.title || "");
    setPrefillArch(exp?.archetype || "");
    setModalOpen(true);
  };

  const handleToggle = (id) => {
    setExpandedId(prev => prev === id ? null : id);
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="experiences-page">
      <Navbar onPlanJourney={() => openLead()} />

      {/* Header */}
      <div className="pt-24 md:pt-32 pb-12 px-6 md:px-12 max-w-7xl mx-auto">
        <p className="overline mb-4">Experience Library</p>
        <h1 className="font-heading text-4xl md:text-6xl font-light text-white tracking-tight mb-4">
          Every Journey <span className="italic text-[#C9A84C]">Begins Within</span>
        </h1>
        <p className="text-[#A1A1AA] text-base max-w-xl">
          Curated experiences designed around who you are, not where the world goes.
        </p>
      </div>

      {/* Filters */}
      <div data-testid="experiences-filters" className="sticky top-[64px] z-30 bg-black/80 backdrop-blur-xl border-b border-white/5 px-6 md:px-12 py-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex md:hidden items-center justify-between">
            <p className="text-xs text-[#A1A1AA] tracking-wider uppercase">{experiences.length} Experiences</p>
            <button
              data-testid="filter-toggle-btn"
              onClick={() => setFilterOpen(!filterOpen)}
              className="flex items-center gap-2 text-xs text-[#C9A84C] uppercase tracking-[0.15em]"
            >
              <Filter size={14} />Filter
            </button>
          </div>

          <div className={`${filterOpen ? "flex" : "hidden"} md:flex flex-col md:flex-row gap-4 md:gap-8 mt-4 md:mt-0`}>
            <div>
              <p className="text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-2">Category</p>
              <div className="flex flex-wrap gap-2">
                {CATEGORIES.map(cat => (
                  <button
                    key={cat}
                    data-testid={`filter-category-${cat.toLowerCase().replace(/[\s&]/g, '-').replace(/-+/g, '-')}`}
                    onClick={() => setFilter("category", cat)}
                    className={`text-xs px-3 py-1.5 rounded-sm border transition-all duration-200 ${
                      selectedCategory === cat
                        ? "border-[#C9A84C] bg-[#C9A84C]/10 text-[#C9A84C]"
                        : "border-white/10 text-[#A1A1AA] hover:border-white/30 hover:text-white"
                    }`}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-2">Soulprint</p>
              <div className="flex flex-wrap gap-2">
                {ARCHETYPES.map(arch => (
                  <button
                    key={arch}
                    data-testid={`filter-archetype-${arch.toLowerCase().replace(/\s+/g, '-')}`}
                    onClick={() => setFilter("archetype", arch)}
                    className={`text-xs px-3 py-1.5 rounded-sm border transition-all duration-200 ${
                      selectedArchetype === arch
                        ? "border-[#C9A84C] bg-[#C9A84C]/10 text-[#C9A84C]"
                        : "border-white/10 text-[#A1A1AA] hover:border-white/30 hover:text-white"
                    }`}
                  >
                    {arch}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Grid */}
      <div className="px-6 md:px-12 py-12 max-w-7xl mx-auto">
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => <div key={`exp-skel-${i}`} className="h-72 bg-[#111111] rounded-xl animate-pulse" />)}
          </div>
        ) : experiences.length === 0 ? (
          <div className="text-center py-20">
            <p className="font-heading text-3xl font-light text-white mb-4">No experiences found</p>
            <p className="text-[#A1A1AA] mb-6">Try adjusting your filters.</p>
            <button onClick={() => setSearchParams({})} className="btn-outline-gold text-xs px-8 py-3">
              Clear Filters
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {experiences.map(exp => (
              <ExperienceCard
                key={exp._id}
                exp={exp}
                onInquire={openLead}
                isExpanded={expandedId === exp._id}
                onToggle={() => handleToggle(exp._id)}
              />
            ))}
          </div>
        )}
      </div>

      <Footer onPlanJourney={() => openLead()} />

      <LeadModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        prefillExperience={prefillExp}
        prefillArchetype={prefillArch}
        source="experiences_page"
      />
    </div>
  );
}

function ExperienceCard({ exp, onInquire, isExpanded, onToggle }) {
  const catColor = CATEGORY_COLORS[exp.category] || "#C9A84C";

  const handleEnquire = (e) => {
    e.stopPropagation();
    onInquire(exp);
  };

  return (
    <div
      data-testid={`exp-card-${exp._id}`}
      className="overflow-hidden rounded-xl bg-[#111111] border border-white/5 transition-all duration-300 hover:border-white/10 cursor-pointer"
      onClick={onToggle}
    >
      {/* Image */}
      <div className="relative h-52 overflow-hidden">
        <img
          src={exp.image_url || DEFAULT_IMG}
          alt={exp.title}
          className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
          loading="lazy"
          onError={e => { e.target.src = DEFAULT_IMG; }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
        <div className="absolute top-3 left-3 flex gap-2 flex-wrap">
          <span
            className="text-[10px] uppercase tracking-[0.15em] px-2.5 py-1 rounded-sm"
            style={{ background: `${catColor}20`, color: catColor, border: `1px solid ${catColor}30` }}
          >
            {exp.category}
          </span>
        </div>
        {/* Expand indicator */}
        <div className="absolute top-3 right-3">
          <div className="w-7 h-7 rounded-full bg-black/50 flex items-center justify-center">
            {isExpanded
              ? <ChevronUp size={13} className="text-[#C9A84C]" />
              : <ChevronDown size={13} className="text-white/60" />
            }
          </div>
        </div>
      </div>

      {/* Card content */}
      <div className="p-5">
        <h3 className="font-heading text-xl font-light text-white mb-2 leading-tight">{exp.title}</h3>

        <div className="flex items-center gap-3 text-[#71717A] text-xs mb-3">
          <div className="flex items-center gap-1"><MapPin size={10} />{exp.location}</div>
          {exp.duration && <div className="flex items-center gap-1"><Clock size={10} />{exp.duration}</div>}
        </div>

        {/* Preview — complete first sentence, no truncation */}
        <p className="text-[#A1A1AA] text-sm leading-relaxed mb-3">
          {getPreviewText(exp.description)}
        </p>

        {/* Expandable content with smooth animation */}
        <div
          className="overflow-hidden transition-all duration-400 ease-in-out"
          style={{ maxHeight: isExpanded ? "600px" : "0", opacity: isExpanded ? 1 : 0 }}
        >
          <div className="pt-3 border-t border-white/5 mt-2">
            {/* Full description */}
            <p className="text-[#A1A1AA] text-sm leading-relaxed mb-4">
              {exp.description}
            </p>

            {/* All highlights */}
            {exp.highlights && exp.highlights.length > 0 && (
              <div className="mb-5">
                <p className="text-[10px] uppercase tracking-[0.2em] text-[#C9A84C] mb-2">Experience Highlights</p>
                <ul className="space-y-1.5">
                  {exp.highlights.map((h, i) => (
                    <li key={`hl-${i}`} className="flex items-start gap-2 text-xs text-[#A1A1AA]">
                      <span className="w-1 h-1 rounded-full bg-[#C9A84C]/60 mt-1.5 shrink-0" />
                      {h}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Archetype tag */}
            {exp.archetype && (
              <p className="text-[10px] uppercase tracking-[0.15em] mb-4" style={{ color: `${catColor}90` }}>
                {exp.archetype} Soulprint
              </p>
            )}

            {/* CTA */}
            <button
              data-testid={`exp-enquire-btn-${exp._id}`}
              onClick={handleEnquire}
              className="w-full btn-gold text-xs py-3"
            >
              Enquire Now
            </button>
          </div>
        </div>

        {/* Collapsed CTA hint */}
        {!isExpanded && (
          <p className="text-[10px] text-[#71717A] tracking-wider text-center">
            Click to explore ↓
          </p>
        )}
      </div>
    </div>
  );
}
